/**
 * An `AppType` descriptor (platform-catalog `contracts/apptype.schema.json`)
 * COMPILED for the configuration engine: the spec validated against the
 * schema this package carries (`apptype.schema.json`, which the backend's
 * AppType processor validates the catalog with too), then
 * checked for what the engine relies on and the schema cannot say
 * (references between fields, components and rules; expressions and
 * templates parsed once; the shapes of `autofix` and of the CloudNativePG
 * types) — the semantic checks of `contracts/validate.py`.
 *
 * FAIL CLOSED: a descriptor the engine cannot read is refused whole
 * (`DescriptorError`, every reason listed), never applied in part. A
 * descriptor sets the bounds and the rules every project of its type is
 * configured within.
 *
 * NO POD BEYOND A NODE: the reservation (`cpuRequest`, `memoryRequest`) of
 * a component or a quota extra — a constant, or the admin maximum of the
 * field it comes from — must fit what one node leaves a pod
 * (`POD_RESERVATION_MAX`): a pod reserving more is never scheduled, and the
 * admin tier would otherwise be allowed to ask it.
 *
 * ROLES (genericity plan, step 1, 2026-10-24): every configurable field
 * placed in its component by role (`roles.ts`), for the generic
 * Configuration tab and the promotion summary; the chart's names and paths
 * (`chart`) and the credentials (`credentials`) with their defaults.
 *
 * NOT SUPPORTED YET, hence refused: `module` rules (rules coded in a type's
 * optional module, platform-catalog `contracts/module.md`): no module exists
 * in this backend, and skipping such a rule would accept what the type
 * forbids.
 */

import Ajv2020 from 'ajv/dist/2020';
import appTypeSchema from './apptype.schema.json';
import { POD_RESERVATION_MAX } from './cluster';
import {
  expressionFields,
  ExpressionError,
  hasOwn,
  parseExpression,
  parseTemplate,
  templateFields,
  templateUsesChanged,
  usesChanged,
  type ExpressionNode,
  type ExpressionValue,
  type Template,
} from './expression';
import {
  resolveFieldRoles,
  type ComponentRoles,
  type FieldRole,
  type FieldRoleBinding,
} from './roles';

export type FieldValue = ExpressionValue;
/** A configuration: one value per field id. */
export type FieldValues = Record<string, FieldValue>;

export type ConfigurationGrant = 'admin' | 'editor';

export const DATABASE_MODES = ['single', 'ha2', 'ha3zones'] as const;
export type DatabaseMode = (typeof DATABASE_MODES)[number];

/** The CloudNativePG instances of each mode (the template's `dbInstances`). */
export const MODE_INSTANCES: Readonly<Record<DatabaseMode, number>> = {
  single: 1,
  ha2: 2,
  ha3zones: 3,
};

export function isDatabaseMode(value: unknown): value is DatabaseMode {
  return (DATABASE_MODES as readonly unknown[]).includes(value);
}

export type FieldType =
  | 'quantity'
  | 'integer'
  | 'boolean'
  | 'enum'
  | 'databaseMode'
  | 'databaseSync';

/** The units a `quantity` is read and written in (`quantityIn`). */
export type QuantityUnit = 'm' | 'Mi' | 'Gi';
export type FieldUnit = QuantityUnit | 's' | '%';

export type FieldBound =
  | { kind: 'range'; min: number; max: number; step: number }
  | { kind: 'values'; values: FieldValue[] };

export interface DescriptorField {
  id: string;
  label: string;
  component: string;
  type: FieldType;
  unit?: FieldUnit;
  /** Dotted in the descriptor, split here. */
  path: string[];
  default?: FieldValue;
  bounds: Partial<Record<ConfigurationGrant, FieldBound>>;
  growOnly: boolean;
  /** Read and written by the configuration (default true). */
  configurable: boolean;
  /** `enum` only: whether its values are numbers (else strings). */
  numericEnum: boolean;
  /**
   * `fields[].when`: whether the field means something for an environment,
   * over its field values (`fieldShown`); absent: always.
   */
  when?: ExpressionNode;
}

/**
 * A feature offered per environment (`spec.features` entry `{ id, when,
 * reason }`): off for an environment whose values make `when` false
 * (`featureEnabled`), `reason` saying why (`featureOffReason`).
 */
export interface FeatureCondition {
  when: ExpressionNode;
  reason?: Template;
}

/**
 * The roles a creation form shows in its high-availability cards, which
 * read no `when`: a field holding one may not have one.
 */
const HA_ROLES: ReadonlySet<string> = new Set([
  'enabled',
  'replicas',
  'hpaEnabled',
  'hpaMin',
  'hpaMax',
  'hpaTargetCpu',
  'hpaScaleDownWindow',
  'databaseMode',
  'databaseSync',
]);

export type RuleGrant = ConfigurationGrant | undefined;

/** An adjustment: `set` receives `value` (README, phase 1). */
export interface AdjustRule {
  kind: 'adjust';
  index: number;
  id?: string;
  grant: RuleGrant;
  when?: ExpressionNode;
  set: string;
  value: ExpressionNode;
  /** The fields whose change triggers it (`set` included, `message` not). */
  fields: Set<string>;
}

/** A `require` with `autofix`: `target` set to `fixedValue` when `when`. */
export interface AutofixRule {
  kind: 'autofix';
  index: number;
  id?: string;
  grant: RuleGrant;
  when: ExpressionNode;
  require: ExpressionNode;
  target: string;
  fixedValue: boolean;
}

/** A `constraint`, or a `require` without `autofix`: a refusal. */
export interface CheckRule {
  kind: 'check';
  index: number;
  /**
   * The rule's optional `id`: the key of its refusal (`rule:<id>`,
   * `plan.ts`), so that a screen can say it in its own words.
   */
  id?: string;
  grant: RuleGrant;
  when?: ExpressionNode;
  test: ExpressionNode;
  message: Template;
  fields: Set<string>;
}

export type DescriptorRule = AdjustRule | AutofixRule | CheckRule;

export interface DescriptorDerived {
  path: string[];
  value: ExpressionNode;
  /** Default: the fields `value` reads. */
  triggers: string[];
  format?: string;
}

/** Millicores and Mi: a constant, or the id of the field holding it. */
export type ResourceRef = number | string;

export const RESOURCE_KEYS = [
  'cpuRequest',
  'cpuLimit',
  'memoryRequest',
  'memoryLimit',
] as const;
export type ResourceKey = (typeof RESOURCE_KEYS)[number];

/** One init container of a quota row (README, Quota). */
export type InitContainer =
  | { kind: 'defaults' }
  | { kind: 'self' }
  | { kind: 'explicit'; resources: Partial<Record<ResourceKey, number>> };

/**
 * One sidecar of a quota row: a container that runs alongside the main one for
 * the pod's whole life (a native sidecar, an init container with
 * `restartPolicy: Always`, such as the Barman Cloud plugin's in a CloudNativePG
 * instance). The scheduler adds it to the main containers, where an init
 * container only counts when it is the largest.
 */
export type SidecarContainer = Exclude<InitContainer, { kind: 'self' }>;

export interface QuotaRowSpec {
  label?: string;
  pods?: ExpressionNode;
  initContainers?: ExpressionNode | InitContainer[];
  sidecars?: SidecarContainer[];
  why?: Template;
}

/**
 * `app`, the application's own; `cnpg`, `valkey`, the standard blocks of the
 * chart contract (waadoo-common); `mariadb`, a simple MySQL/MariaDB, one
 * instance without high availability (genericity plan §3).
 *
 * `redis`, the kind's name before waadoo-common 0.4.0, is accepted in a
 * descriptor as a DEPRECATED alias of `valkey` (Valkey renaming plan,
 * 2026-10-25, decision 1) and never reaches the engine: `compileDescriptor`
 * turns it into `valkey` (`componentKind`).
 */
export type ComponentKind = 'app' | 'cnpg' | 'valkey' | 'mariadb';

/** A kind as a descriptor may write it: a `ComponentKind`, or an alias. */
type RawComponentKind = ComponentKind | 'redis';

/** The kind a descriptor's kind stands for, its deprecated alias resolved. */
function componentKind(raw: RawComponentKind): ComponentKind {
  return raw === 'redis' ? 'valkey' : raw;
}

/**
 * The block names a Valkey component's values may carry: `valkey`, and
 * `redis`, the name before waadoo-common 0.4.0, which 0.4.x still reads
 * (over its `valkey` defaults) during the alias period (Valkey renaming
 * plan, « Contrat commun »; `waadoo-common.valkey.values` of `_valkey.tpl`).
 */
export const VALKEY_BLOCK_NAMES = ['valkey', 'redis'] as const;

export interface DescriptorComponent {
  id: string;
  label: string;
  kind: ComponentKind;
  valuesPath: string[];
  pods: ExpressionNode;
  enabled?: ExpressionNode;
  resources: Partial<Record<ResourceKey, ResourceRef>>;
  quota: QuotaRowSpec;
  /**
   * The labels selecting its pods: `components[].podSelector`, else the
   * default of its kind (`defaultPodSelector`). A value may hold
   * `{fullname}` (the Helm fullname), left for the consumer to resolve.
   */
  podSelector: Readonly<Record<string, string>>;
  /** `components[].group` (an `app` component's compact section), if any. */
  group?: string;
  /**
   * A `valkey` component whose `valuesPath` ends in one of
   * `VALKEY_BLOCK_NAMES`: the same path ending in the other name, where an
   * environment's values may still (or already) hold the block during the
   * alias period. Read and written there when the block is there and not at
   * `valuesPath` (values.ts, `valuesBlockPaths`).
   */
  valuesPathAlias?: string[];
}

/** A chart-specific name with a length limit (`chart.resourceNames`). */
export interface ChartResourceName {
  what: string;
  /** Contains `{fullname}`, the only placeholder. */
  name: string;
  max: number;
}

/** `spec.chart`, what the backend derives names and paths from. */
export interface DescriptorChart {
  /**
   * `chart.name`: the chart's name (`.Chart.Name`), from which its fullname
   * is built (`waadoo-common.fullname`).
   */
  name: string;
  valuesRoot: string;
  component: string;
  fullnameSuffix: string;
  hostPrefix: string;
  /**
   * `chart.imageTagPath`, split; default
   * `<valuesRoot>.<component>.image.tag` (the values contract).
   */
  imageTagPath: string[];
  /** `chart.resourceNames`, in order; empty when absent. */
  resourceNames: ChartResourceName[];
  /**
   * `chart.sourceGenerationSince`: the first version (exact `X.Y.Z`) of the
   * chart that reads `cnpg.recovery.sourceGeneration`, the restore of a
   * point in time from an older generation's Barman files; absent: the
   * type does not say, and such a restore is refused.
   */
  sourceGenerationSince?: string;
}

/** `spec.credentials`, its defaults applied (`login`, `password`). */
export interface DescriptorCredentials {
  /**
   * A literal name, or a template where `{fullname}` is the only placeholder
   * allowed; absent: the Secret is found by its keys.
   */
  secret?: string;
  userKey: string;
  passwordKey: string;
  loginPath?: string;
}

export type QuotaScope = 'permanent' | 'ephemeral' | 'both';

export interface DescriptorQuotaExtra {
  id: string;
  label?: string;
  when?: ExpressionNode;
  scope: QuotaScope;
  pods: ExpressionNode;
  resources: Record<ResourceKey, number>;
  resourcesFrom?: string[];
  quota: QuotaRowSpec;
}

export type QuotaItem =
  | { kind: 'component'; item: DescriptorComponent }
  | { kind: 'extra'; item: DescriptorQuotaExtra };

/**
 * Le profil d'un type (`spec.profile`, exigé par le schéma) : `developed`,
 * un code propre au projet (dépôt applicatif, CI d'image, branches
 * éphémères) ; `standard`, une version et une configuration, sans dépôt de
 * code ni CI d'image (platform-catalog `contracts/apptype.schema.json`).
 */
export type AppProfile = 'developed' | 'standard';

/** Les rôles d'un modèle d'environnement (`spec.templates.env`). */
export const ENV_TEMPLATE_ROLES = [
  'configure',
  'promote',
  'restore',
  'sleep',
  'wake',
] as const;
export type EnvTemplateRole = (typeof ENV_TEMPLATE_ROLES)[number];

/** A capacity profile (`spec.profiles`): values of configurable fields. */
export interface DescriptorProfile {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  /** The profile a creation form picks first. */
  default: boolean;
  values: FieldValues;
}

/**
 * An `AppType` as written — `metadata.name`, `metadata.title`, `spec` — what
 * `compileDescriptor` reads. Plain JSON, unlike the compiled descriptor
 * (maps, sets, parsed expressions): what the backend sends the frontend,
 * which compiles it with this same engine.
 */
export interface AppTypeSource {
  name: string;
  title: string;
  spec: unknown;
}

export interface AppTypeDescriptor {
  name: string;
  title: string;
  /** What it was compiled from, as given. */
  source: AppTypeSource;
  /**
   * The component whose block is the chart's main one
   * (`<chart.valuesRoot>.<chart.component>`), if any.
   */
  mainComponent?: DescriptorComponent;
  /** The CloudNativePG component (`kind: cnpg`), if any. */
  databaseComponent?: DescriptorComponent;
  /** The MySQL/MariaDB component (`kind: mariadb`), if any. */
  mariadbComponent?: DescriptorComponent;
  /** `spec.chart`, defaults applied. */
  chart: DescriptorChart;
  /** `spec.credentials`, defaults applied (also when absent). */
  credentials: DescriptorCredentials;
  /**
   * The role of every configurable field, by id, in the descriptor's order
   * (`roles.ts`): its component, and `role` with its `roleSource` when it
   * has one.
   */
  fieldRoles: ReadonlyMap<string, FieldRoleBinding>;
  /**
   * Every component, by id, in the descriptor's order: role → field id, and
   * its volumes — the sections of the generic Configuration tab.
   */
  componentRoles: ReadonlyMap<string, ComponentRoles>;
  /** The configurable fields without a role, in order: « Autres réglages ». */
  unclassifiedFields: string[];
  /** Every field, in the descriptor's order (README: the order counts). */
  fields: DescriptorField[];
  fieldById: ReadonlyMap<string, DescriptorField>;
  /** The fields of the configuration, in order (`configurable` not false). */
  configurableFields: DescriptorField[];
  rules: DescriptorRule[];
  derived: DescriptorDerived[];
  components: DescriptorComponent[];
  quotaExtras: DescriptorQuotaExtra[];
  /** The rows of `quota.yaml`, in `spec.quota.order` (or components, extras). */
  quotaOrder: QuotaItem[];
  volumes: { count: ExpressionNode; storage: ExpressionNode };
  /** `branches.ephemeral.lightweight`: forced on an ephemeral branch. */
  lightweight: FieldValues;
  /** `spec.profiles`, in the descriptor's order. */
  profiles: DescriptorProfile[];
  /** Whether the spec declares `quota` (absent from pre-engine descriptors). */
  declaresQuota: boolean;
  /**
   * `spec.features`: the core functions and optional modules declared, by
   * id — for the TYPE: a feature with a `when` is in it, and may still be
   * off for an environment (`featureConditions`, `featureEnabled`).
   */
  features: ReadonlySet<string>;
  /** The `when` (and `reason`) of the features declared with one, by id. */
  featureConditions: ReadonlyMap<string, FeatureCondition>;
  /** `spec.profile`. */
  profile: AppProfile;
  /**
   * Les modèles que `spec.templates.env` désigne, par rôle, en minuscules
   * comme le scaffolder écrit la référence du modèle d'une tâche (le
   * `declaredTemplates` du backend, `templateRoles.ts`). Un rôle absent :
   * aucun modèle désigné.
   */
  envTemplates: Partial<Record<EnvTemplateRole, string>>;
  /**
   * Le type garde des données : un composant CloudNativePG ou MariaDB, ou un
   * `quota.volumes.count` qui n'est pas le littéral 0 (absent : 0). Une
   * expression vaut « persistant » quelle que soit sa valeur : fail closed,
   * un type qui pourrait avoir un volume est traité comme en ayant un.
   */
  persistent: boolean;
}

/**
 * No descriptor can configure the type: none in the catalog for a type other
 * than Odoo, an unreadable catalog for such a type, or one the engine cannot
 * compile (`DescriptorError`). The configuration route answers it 409.
 */
export class NoDescriptorError extends Error {}

/** Why a descriptor cannot be used, every reason listed. */
export class DescriptorError extends NoDescriptorError {
  constructor(readonly appType: string, readonly problems: string[]) {
    super(
      `Le descripteur AppType ${appType} ne peut pas être utilisé par le moteur de configuration : ${problems.join(
        ' ; ',
      )}`,
    );
  }
}

// --- The spec as the schema defines it ------------------------------------------

type RawResources = Partial<Record<ResourceKey, number | string>>;

interface RawQuotaRow {
  label?: string;
  pods?: number | string;
  initContainers?:
    | number
    | string
    | Array<'defaults' | 'self' | Partial<Record<ResourceKey, number>>>;
  sidecars?: Array<'defaults' | Partial<Record<ResourceKey, number>>>;
  why?: string;
}

interface RawBound {
  min?: number;
  max?: number;
  step?: number;
  values?: FieldValue[];
}

interface RawSpec {
  profile: AppProfile;
  chart: {
    name: string;
    valuesRoot: string;
    component: string;
    fullnameSuffix: string;
    hostPrefix: string;
    imageTagPath?: string;
    resourceNames?: ChartResourceName[];
    sourceGenerationSince?: string;
  };
  templates: {
    env?: Partial<Record<EnvTemplateRole, string>>;
  };
  branches?: { ephemeral?: { lightweight?: FieldValues } };
  components: Array<{
    id: string;
    label: string;
    kind: RawComponentKind;
    valuesPath: string;
    podSelector?: Record<string, string>;
    group?: string;
    pods?: number | string;
    quota?: RawQuotaRow;
    enabled?: string;
    resources?: RawResources;
  }>;
  fields: Array<{
    id: string;
    label: string;
    component: string;
    type: FieldType;
    unit?: FieldUnit;
    path: string;
    default?: FieldValue;
    bounds?: Partial<Record<ConfigurationGrant, RawBound>>;
    growOnly?: boolean;
    configurable?: boolean;
    role?: FieldRole;
    mountedBy?: string[];
    when?: string;
  }>;
  derived?: Array<{
    path: string;
    value: string;
    triggers?: string[];
    format?: string;
  }>;
  rules?: Array<{
    id?: string;
    when?: string;
    constraint?: string;
    require?: string;
    autofix?: boolean;
    set?: string;
    value?: string;
    grant?: ConfigurationGrant;
    message?: string;
    module?: string;
  }>;
  quotaExtras?: Array<{
    id: string;
    label?: string;
    when?: string;
    scope?: QuotaScope;
    pods?: number;
    resources: Record<ResourceKey, number>;
    resourcesFrom?: string;
    quota?: RawQuotaRow;
  }>;
  quota?: {
    order?: string[];
    volumes?: { count: number | string; storage: number | string };
  };
  profiles?: Record<
    string,
    {
      label: string;
      description?: string;
      icon?: string;
      default?: boolean;
      values: FieldValues;
    }
  >;
  credentials?: Partial<DescriptorCredentials>;
  features: Array<string | { id: string; when: string; reason?: string }>;
}

/**
 * The schema's `spec`, compiled once. `allowUnionTypes` as the catalog
 * processor (`appTypeProcessor.ts`), which validated the same spec at
 * ingestion; validated again here because the engine also reads descriptors
 * the catalog did not (the embedded one) and must never trust a shape.
 */
/** The contract, for the backend's AppType processor too (one copy). */
export const APP_TYPE_SCHEMA = appTypeSchema;

const ajv = new Ajv2020({ allErrors: true, allowUnionTypes: true });
ajv.addSchema(appTypeSchema);
const validateSpec = ajv.compile<RawSpec>({
  $ref: `${appTypeSchema.$id}#/properties/spec`,
});

const RESERVED = new Set([
  'min',
  'max',
  'floor',
  'ceil',
  'if',
  'concat',
  'changed',
  'true',
  'false',
]);

const QUANTITY_UNITS: ReadonlySet<string> = new Set(['m', 'Mi', 'Gi']);

/**
 * `spec` of the AppType `name` compiled, or a `DescriptorError` thrown with
 * every problem found.
 */
export function compileDescriptor(input: AppTypeSource): AppTypeDescriptor {
  const { name } = input;
  if (!validateSpec(input.spec)) {
    throw new DescriptorError(
      name,
      (validateSpec.errors ?? []).map(
        error =>
          `schéma ${error.instancePath || '/'} : ${
            error.message ?? error.keyword
          }`,
      ),
    );
  }
  const spec = input.spec;
  const problems: string[] = [];
  const fail = (message: string) => problems.push(message);

  const fieldIds = new Set<string>();
  for (const field of spec.fields) {
    if (fieldIds.has(field.id)) fail(`champ en double : ${field.id}`);
    if (RESERVED.has(field.id)) fail(`identifiant réservé : ${field.id}`);
    fieldIds.add(field.id);
  }
  const componentIds = new Set<string>();
  for (const component of spec.components) {
    if (componentIds.has(component.id)) {
      fail(`composant en double : ${component.id}`);
    }
    componentIds.add(component.id);
  }

  /** Parses `source`, its fields known and `changed()` only if allowed. */
  const expression = (
    where: string,
    source: string | number,
    allowChanged: boolean,
  ): ExpressionNode => {
    if (typeof source === 'number') {
      return { kind: 'literal', value: source };
    }
    try {
      const node = parseExpression(source);
      const unknown = [...expressionFields(node)].filter(
        id => !fieldIds.has(id),
      );
      if (unknown.length > 0) {
        fail(`${where} : champ(s) inconnu(s) ${unknown.join(', ')}`);
      }
      if (!allowChanged && usesChanged(node)) {
        fail(`${where} : changed() n’est permis que dans les règles`);
      }
      return node;
    } catch (error: unknown) {
      fail(`${where} : ${messageOf(error)}`);
      return { kind: 'literal', value: false };
    }
  };
  const template = (
    where: string,
    source: string,
    allowChanged: boolean,
  ): Template => {
    try {
      const parsed = parseTemplate(source);
      const unknown = [...templateFields(parsed)].filter(
        id => !fieldIds.has(id),
      );
      if (unknown.length > 0) {
        fail(`${where} : champ(s) inconnu(s) ${unknown.join(', ')}`);
      }
      if (!allowChanged && templateUsesChanged(parsed)) {
        fail(`${where} : changed() n’est permis que dans les règles`);
      }
      return parsed;
    } catch (error: unknown) {
      fail(`${where} : ${messageOf(error)}`);
      return [];
    }
  };
  const quotaRow = (where: string, raw: RawQuotaRow | undefined) => {
    const row: QuotaRowSpec = {};
    if (!raw) return row;
    if (raw.label !== undefined) row.label = raw.label;
    if (raw.pods !== undefined) {
      row.pods = expression(`${where} quota.pods`, raw.pods, false);
    }
    if (Array.isArray(raw.initContainers)) {
      row.initContainers = raw.initContainers.map(item =>
        item === 'defaults' || item === 'self'
          ? { kind: item }
          : { kind: 'explicit' as const, resources: item },
      );
    } else if (raw.initContainers !== undefined) {
      row.initContainers = expression(
        `${where} quota.initContainers`,
        raw.initContainers,
        false,
      );
    }
    if (raw.sidecars !== undefined) {
      row.sidecars = raw.sidecars.map(item =>
        item === 'defaults'
          ? { kind: item }
          : { kind: 'explicit' as const, resources: item },
      );
    }
    if (raw.why !== undefined) {
      row.why = template(`${where} quota.why`, raw.why, false);
    }
    return row;
  };

  const mainPath = `${spec.chart.valuesRoot}.${spec.chart.component}`;
  const components: DescriptorComponent[] = spec.components.map(raw => {
    const where = `composant ${raw.id}`;
    const resources: Partial<Record<ResourceKey, ResourceRef>> = {};
    for (const key of RESOURCE_KEYS) {
      const value = raw.resources?.[key];
      if (value === undefined) continue;
      if (typeof value === 'string' && !fieldIds.has(value)) {
        fail(`${where} resources.${key} : champ inconnu ${value}`);
      }
      resources[key] = value;
    }
    const kind = componentKind(raw.kind);
    if (raw.group !== undefined && kind !== 'app') {
      fail(`${where} : group n’est permis que sur un composant app`);
    }
    const pods = expression(`${where} pods`, raw.pods ?? 1, false);
    if (kind === 'mariadb' && !(pods.kind === 'literal' && pods.value === 1)) {
      fail(
        `${where} : mariadb, une instance sans haute disponibilité (pods : 1)`,
      );
    }
    const valuesPath = raw.valuesPath.split('.');
    const valuesPathAlias =
      kind === 'valkey' ? valkeyPathAlias(valuesPath) : undefined;
    return {
      id: raw.id,
      label: raw.label,
      kind,
      valuesPath,
      pods,
      ...(raw.enabled !== undefined
        ? { enabled: expression(`${where} enabled`, raw.enabled, false) }
        : {}),
      resources,
      quota: quotaRow(where, raw.quota),
      podSelector:
        raw.podSelector ??
        defaultPodSelector(
          { id: raw.id, kind },
          raw.valuesPath === mainPath ? spec.chart.component : undefined,
        ),
      ...(raw.group !== undefined ? { group: raw.group } : {}),
      ...(valuesPathAlias ? { valuesPathAlias } : {}),
    };
  });
  const cnpg = components.filter(component => component.kind === 'cnpg');
  if (cnpg.length > 1) {
    fail('un seul composant cnpg par type');
  }
  const mariadb = components.filter(component => component.kind === 'mariadb');
  if (mariadb.length > 1) {
    fail('un seul composant mariadb par type');
  }
  const mainComponent = components.find(
    component => component.valuesPath.join('.') === mainPath,
  );

  const fields: DescriptorField[] = spec.fields.map(raw => {
    const where = `champ ${raw.id}`;
    if (!componentIds.has(raw.component)) {
      fail(`${where} : composant inconnu ${raw.component}`);
    }
    if (raw.type === 'quantity' && !QUANTITY_UNITS.has(raw.unit ?? '')) {
      fail(`${where} : une quantité s’exprime en m, Mi ou Gi`);
    }
    if (
      (raw.type === 'databaseMode' || raw.type === 'databaseSync') &&
      (cnpg.length !== 1 || raw.path !== cnpg[0].valuesPath.join('.'))
    ) {
      fail(`${where} : ${raw.type} doit pointer le bloc du composant cnpg`);
    }
    const bounds: Partial<Record<ConfigurationGrant, FieldBound>> = {};
    for (const grant of ['editor', 'admin'] as const) {
      const bound = raw.bounds?.[grant];
      if (!bound) continue;
      if (bound.values !== undefined) {
        bounds[grant] = { kind: 'values', values: bound.values };
      } else if (
        bound.min !== undefined &&
        bound.max !== undefined &&
        bound.step !== undefined
      ) {
        bounds[grant] = {
          kind: 'range',
          min: bound.min,
          max: bound.max,
          step: bound.step,
        };
      }
    }
    if (
      (raw.type === 'quantity' ||
        raw.type === 'integer' ||
        raw.type === 'enum') &&
      raw.configurable !== false &&
      (!bounds.editor || !bounds.admin)
    ) {
      fail(`${where} : type ${raw.type} sans bornes editor et admin`);
    }
    const enumValues = [
      ...(bounds.editor?.kind === 'values' ? bounds.editor.values : []),
      ...(bounds.admin?.kind === 'values' ? bounds.admin.values : []),
    ];
    const numericEnum =
      raw.type === 'enum' &&
      enumValues.every(value => typeof value === 'number');
    if (
      raw.type === 'enum' &&
      !numericEnum &&
      !enumValues.every(value => typeof value === 'string')
    ) {
      fail(
        `${where} : les valeurs d’un enum sont toutes des nombres ou toutes des chaînes`,
      );
    }
    return {
      id: raw.id,
      label: raw.label,
      component: raw.component,
      type: raw.type,
      ...(raw.unit !== undefined ? { unit: raw.unit } : {}),
      path: raw.path.split('.'),
      ...(raw.default !== undefined ? { default: raw.default } : {}),
      bounds,
      growOnly: raw.growOnly === true,
      configurable: raw.configurable !== false,
      numericEnum,
      ...(raw.when !== undefined
        ? { when: expression(`${where} when`, raw.when, false) }
        : {}),
    };
  });
  const fieldById = new Map(fields.map(field => [field.id, field]));
  const { fieldRoles, componentRoles, unclassifiedFields } = resolveFieldRoles(
    components,
    fields,
    spec.fields,
    fail,
  );
  for (const field of fields) {
    if (!field.when) continue;
    const where = `champ ${field.id} when`;
    if (!field.configurable) {
      fail(`${where} : seulement sur un champ configurable`);
    }
    const role = fieldRoles.get(field.id)?.role;
    if (role !== undefined && HA_ROLES.has(role)) {
      fail(
        `${where} : pas sur un champ de la haute disponibilité (rôle ${role})`,
      );
    }
    if (expressionFields(field.when).has(field.id)) {
      fail(`${where} : ne peut pas lire le champ lui-même`);
    }
  }
  const reservations = [
    ['cpuRequest', POD_RESERVATION_MAX.cpu, 'm'],
    ['memoryRequest', POD_RESERVATION_MAX.memory, 'Mi'],
  ] as const;
  const podGuard = (
    where: string,
    resources: Partial<Record<ResourceKey, number | string>>,
  ) => {
    for (const [key, limit, unit] of reservations) {
      const ref = resources[key];
      if (typeof ref === 'number') {
        if (ref > limit) {
          fail(
            `${where} resources.${key} : ${ref} ${unit}, au-delà de ce qu’un pod peut réserver sur un nœud (${limit} ${unit})`,
          );
        }
        continue;
      }
      const field = ref === undefined ? undefined : fieldById.get(ref);
      if (!field) continue;
      if (field.unit !== unit) {
        fail(
          `${where} resources.${key} : le champ ${field.id} doit être en ${unit}`,
        );
        continue;
      }
      const admin = field.bounds.admin;
      const max =
        admin?.kind === 'range'
          ? admin.max
          : Math.max(
              ...(admin?.values ?? []).filter(
                (value): value is number => typeof value === 'number',
              ),
            );
      if (!Number.isFinite(max)) {
        fail(
          `${where} resources.${key} : le champ ${field.id} n’a pas de borne admin`,
        );
      } else if (max > limit) {
        fail(
          `champ ${field.id} : borne admin ${max} ${unit}, au-delà de ce qu’un pod peut réserver sur un nœud (${limit} ${unit})`,
        );
      }
    }
  };
  for (const component of components) {
    podGuard(`composant ${component.id}`, component.resources);
  }
  for (const extra of spec.quotaExtras ?? []) {
    podGuard(`extra ${extra.id}`, extra.resources);
  }
  const configurable = (id: string) => fieldById.get(id)?.configurable;
  for (const type of ['databaseMode', 'databaseSync'] as const) {
    if (fields.filter(field => field.type === type).length > 1) {
      fail(`un seul champ ${type} par type`);
    }
  }
  if (
    fields.some(field => field.type === 'databaseSync') &&
    !fields.some(field => field.type === 'databaseMode')
  ) {
    fail('databaseSync sans champ databaseMode');
  }

  const rules: DescriptorRule[] = [];
  const ruleIds = new Set<string>();
  (spec.rules ?? []).forEach((raw, index) => {
    const where = `règle ${index}`;
    if (raw.id !== undefined) {
      if (ruleIds.has(raw.id))
        fail(`${where} : id de règle en double ${raw.id}`);
      ruleIds.add(raw.id);
    }
    const id = raw.id !== undefined ? { id: raw.id } : {};
    if (raw.module !== undefined) {
      fail(
        `${where} : règle de module ${raw.module}, aucun module de règles n’existe dans ce portail`,
      );
      return;
    }
    const when =
      raw.when !== undefined
        ? expression(`${where} when`, raw.when, true)
        : undefined;
    const grant = raw.grant;
    if (raw.set !== undefined && raw.value !== undefined) {
      if (!configurable(raw.set)) {
        fail(`${where} : set vise un champ inconnu ou non configurable`);
      }
      const value = expression(`${where} value`, raw.value, true);
      rules.push({
        kind: 'adjust',
        index,
        ...id,
        grant,
        ...(when ? { when } : {}),
        set: raw.set,
        value,
        fields: new Set([
          ...(when ? expressionFields(when) : []),
          ...expressionFields(value),
          raw.set,
        ]),
      });
      return;
    }
    const source = raw.constraint ?? raw.require;
    if (source === undefined) {
      fail(`${where} : ni constraint, ni require, ni set`);
      return;
    }
    const test = expression(`${where}`, source, true);
    if (raw.require !== undefined && raw.autofix === true) {
      const match = /^\s*(!?)\s*([A-Za-z_][A-Za-z0-9_]*)\s*$/.exec(raw.require);
      const target = match ? fieldById.get(match[2]) : undefined;
      if (
        !match ||
        !target ||
        !target.configurable ||
        (target.type !== 'boolean' && target.type !== 'databaseSync') ||
        !when
      ) {
        fail(
          `${where} : autofix exige un champ booléen configurable ou sa négation (${JSON.stringify(
            raw.require,
          )})`,
        );
        return;
      }
      rules.push({
        kind: 'autofix',
        index,
        ...id,
        grant,
        when,
        require: test,
        target: target.id,
        fixedValue: match[1] !== '!',
      });
      return;
    }
    if (raw.message === undefined) {
      fail(`${where} : refus sans message`);
      return;
    }
    rules.push({
      kind: 'check',
      index,
      ...id,
      grant,
      ...(when ? { when } : {}),
      test,
      message: template(`${where} message`, raw.message, true),
      fields: new Set([
        ...(when ? expressionFields(when) : []),
        ...expressionFields(test),
      ]),
    });
  });

  const derived: DescriptorDerived[] = (spec.derived ?? []).map(raw => {
    const where = `dérivée ${raw.path}`;
    const value = expression(where, raw.value, false);
    const triggers = raw.triggers ?? [...expressionFields(value)];
    for (const trigger of triggers) {
      if (!configurable(trigger)) {
        fail(`${where} : déclencheur inconnu ou non configurable ${trigger}`);
      }
    }
    if (raw.format !== undefined && raw.format.split('{value}').length !== 2) {
      fail(`${where} : format sans un seul {value}`);
    }
    return {
      path: raw.path.split('.'),
      value,
      triggers,
      ...(raw.format !== undefined ? { format: raw.format } : {}),
    };
  });

  const quotaExtras: DescriptorQuotaExtra[] = (spec.quotaExtras ?? []).map(
    raw => {
      const where = `extra ${raw.id}`;
      if (componentIds.has(raw.id)) {
        fail(`${where} : id déjà pris par un composant`);
      }
      return {
        id: raw.id,
        ...(raw.label !== undefined ? { label: raw.label } : {}),
        ...(raw.when !== undefined
          ? { when: expression(`${where} when`, raw.when, false) }
          : {}),
        scope: raw.scope ?? 'both',
        pods: expression(`${where} pods`, raw.pods ?? 1, false),
        resources: raw.resources,
        ...(raw.resourcesFrom !== undefined
          ? { resourcesFrom: raw.resourcesFrom.split('.') }
          : {}),
        quota: quotaRow(where, raw.quota),
      };
    },
  );
  const extraIds = quotaExtras.map(extra => extra.id);
  if (new Set(extraIds).size !== extraIds.length) {
    fail('extra de quota en double');
  }

  const items = new Map<string, QuotaItem>([
    ...components.map(
      item => [item.id, { kind: 'component', item }] as [string, QuotaItem],
    ),
    ...quotaExtras.map(
      item => [item.id, { kind: 'extra', item }] as [string, QuotaItem],
    ),
  ]);
  const order = spec.quota?.order ?? [...items.keys()];
  const unknownRows = order.filter(id => !items.has(id));
  const missingRows = [...items.keys()].filter(id => !order.includes(id));
  if (unknownRows.length > 0) {
    fail(`quota.order : id inconnu(s) ${unknownRows.join(', ')}`);
  }
  if (missingRows.length > 0) {
    fail(`quota.order : absent(s) ${missingRows.join(', ')}`);
  }
  const quotaOrder = order.flatMap(id => {
    const item = items.get(id);
    return item ? [item] : [];
  });
  const rawVolumes = spec.quota?.volumes;
  const volumes = {
    count: expression('quota.volumes.count', rawVolumes?.count ?? 0, false),
    storage: expression(
      'quota.volumes.storage',
      rawVolumes?.storage ?? 0,
      false,
    ),
  };

  // MariaDB's volume: a size field, when it has one (a constant size in
  // quota.volumes.storage is allowed, as for cnpg), counted in the storage.
  for (const component of mariadb) {
    const size = componentRoles.get(component.id)?.roles.databaseSize;
    if (size !== undefined && !expressionFields(volumes.storage).has(size)) {
      fail(
        `quota.volumes.storage : ne compte pas ${size}, le volume du composant mariadb ${component.id}`,
      );
    }
  }

  const chart: DescriptorChart = {
    name: spec.chart.name,
    valuesRoot: spec.chart.valuesRoot,
    component: spec.chart.component,
    fullnameSuffix: spec.chart.fullnameSuffix,
    hostPrefix: spec.chart.hostPrefix,
    imageTagPath: (
      spec.chart.imageTagPath ??
      `${spec.chart.valuesRoot}.${spec.chart.component}.image.tag`
    ).split('.'),
    resourceNames: spec.chart.resourceNames ?? [],
    ...(spec.chart.sourceGenerationSince !== undefined
      ? { sourceGenerationSince: spec.chart.sourceGenerationSince }
      : {}),
  };
  const whats = new Set<string>();
  for (const { what, name: resourceName } of chart.resourceNames) {
    if (whats.has(what)) fail(`chart.resourceNames : ${what} en double`);
    whats.add(what);
    if (!fullnameTemplate(resourceName)) {
      fail(
        `chart.resourceNames ${what} : le nom doit contenir {fullname}, seul gabarit permis (${JSON.stringify(
          resourceName,
        )})`,
      );
    }
  }
  const rawCredentials = spec.credentials ?? {};
  if (
    rawCredentials.secret !== undefined &&
    !onlyFullnameTemplate(rawCredentials.secret)
  ) {
    fail(
      `credentials.secret : {fullname} est le seul gabarit permis (${JSON.stringify(
        rawCredentials.secret,
      )})`,
    );
  }
  const credentials: DescriptorCredentials = {
    ...(rawCredentials.secret !== undefined
      ? { secret: rawCredentials.secret }
      : {}),
    userKey: rawCredentials.userKey ?? 'login',
    passwordKey: rawCredentials.passwordKey ?? 'password',
    ...(rawCredentials.loginPath !== undefined
      ? { loginPath: rawCredentials.loginPath }
      : {}),
  };

  const lightweight = spec.branches?.ephemeral?.lightweight ?? {};
  for (const id of Object.keys(lightweight)) {
    if (!fieldIds.has(id)) fail(`version légère : champ inconnu ${id}`);
  }

  const profiles: DescriptorProfile[] = Object.entries(spec.profiles ?? {}).map(
    ([id, raw]) => {
      for (const field of Object.keys(raw.values)) {
        if (!configurable(field)) {
          fail(`profil ${id} : champ inconnu ou non configurable ${field}`);
        }
      }
      return {
        id,
        label: raw.label,
        ...(raw.description !== undefined
          ? { description: raw.description }
          : {}),
        ...(raw.icon !== undefined ? { icon: raw.icon } : {}),
        default: raw.default === true,
        values: raw.values,
      };
    },
  );

  const envTemplates: Partial<Record<EnvTemplateRole, string>> = {};
  for (const role of ENV_TEMPLATE_ROLES) {
    const ref = spec.templates.env?.[role];
    if (ref !== undefined) {
      envTemplates[role] = ref.toLowerCase();
    }
  }
  const features = new Set<string>();
  const featureConditions = new Map<string, FeatureCondition>();
  for (const entry of spec.features) {
    const id = typeof entry === 'string' ? entry : entry.id;
    if (features.has(id)) fail(`fonction en double : ${id}`);
    features.add(id);
    if (typeof entry === 'string') continue;
    // The schema allows the object form for `uploads` only: the feature
    // whose every consumer evaluates the `when` (the workflows' row of the
    // quota, the upload card). Another one's consumers read `features` only
    // and would offer it where the type says it is off.
    const where = `fonction ${id}`;
    featureConditions.set(id, {
      when: expression(`${where} when`, entry.when, false),
      ...(entry.reason !== undefined
        ? { reason: template(`${where} reason`, entry.reason, false) }
        : {}),
    });
  }
  const persistent =
    cnpg.length > 0 ||
    mariadb.length > 0 ||
    !(volumes.count.kind === 'literal' && volumes.count.value === 0);

  if (problems.length > 0) {
    throw new DescriptorError(name, problems);
  }
  return {
    name,
    title: input.title,
    source: input,
    ...(mainComponent ? { mainComponent } : {}),
    ...(cnpg.length === 1 ? { databaseComponent: cnpg[0] } : {}),
    ...(mariadb.length === 1 ? { mariadbComponent: mariadb[0] } : {}),
    chart,
    credentials,
    fieldRoles,
    componentRoles,
    unclassifiedFields,
    fields,
    fieldById,
    configurableFields: fields.filter(field => field.configurable),
    rules,
    derived,
    components,
    quotaExtras,
    quotaOrder,
    volumes,
    lightweight,
    profiles,
    declaresQuota: spec.quota !== undefined,
    features,
    featureConditions,
    profile: spec.profile,
    envTemplates,
    persistent,
  };
}

/** Whether `template` has no placeholder but `{fullname}` (maybe none). */
function onlyFullnameTemplate(template: string): boolean {
  return template.split('{fullname}').every(part => !/[{}]/.test(part));
}

/** Whether `template` contains `{fullname}` and no other placeholder. */
function fullnameTemplate(template: string): boolean {
  return template.includes('{fullname}') && onlyFullnameTemplate(template);
}

const COMPONENT_LABEL = 'app.kubernetes.io/component';

/**
 * `path` ending in the other of `VALKEY_BLOCK_NAMES` (`odoo.valkey` ↔
 * `odoo.redis`), or undefined when it ends in neither.
 */
function valkeyPathAlias(path: readonly string[]): string[] | undefined {
  const [current, other] = VALKEY_BLOCK_NAMES;
  const last = path[path.length - 1];
  if (last !== current && last !== other) {
    return undefined;
  }
  return [...path.slice(0, -1), last === current ? other : current];
}

/**
 * The labels the platform's charts put on a component's pods
 * (helm/charts waadoo-common): the main component's
 * `app.kubernetes.io/component` is the chart's name (`chartComponent`,
 * `componentLabels` of the chart's deployment), another app component's its
 * id; Valkey `valkey` (waadoo-common 0.4.0 — `redis` before it: the
 * selector is a plain label map, it cannot say « valkey or redis », and
 * nothing matches pods on it yet, `podNames.ts` matching by name, both
 * names); CloudNativePG labels its instances
 * `cnpg.io/cluster=<fullname>-db` (`waadoo-common.dbCluster`) and
 * `cnpg.io/podRole=instance`, which leaves the poolers out — its
 * `app.kubernetes.io/component=database` is on the Cluster, not on the pods.
 * MariaDB `mariadb` (`waadoo-common.mariadb.podLabels` of `_mariadb.tpl`,
 * waadoo-common 0.2.0), which leaves out the pods of its Secret's init Job,
 * labelled `mariadb-init`.
 */
function defaultPodSelector(
  raw: { id: string; kind: ComponentKind },
  chartComponent: string | undefined,
): Record<string, string> {
  if (raw.kind === 'cnpg') {
    return {
      'cnpg.io/cluster': '{fullname}-db',
      'cnpg.io/podRole': 'instance',
    };
  }
  if (raw.kind === 'valkey' || raw.kind === 'mariadb') {
    return { [COMPONENT_LABEL]: raw.kind };
  }
  return { [COMPONENT_LABEL]: chartComponent ?? raw.id };
}

function messageOf(error: unknown): string {
  return error instanceof ExpressionError || error instanceof Error
    ? error.message
    : String(error);
}

/**
 * The value of field `id` in `values`, its OWN property only: a field id
 * such as `constructor` never reads `Object.prototype`.
 */
export function valueOf(
  values: Readonly<Record<string, FieldValue | undefined>>,
  id: string,
): FieldValue | undefined {
  return hasOwn(values, id) ? values[id] : undefined;
}

/** Whether `value` is within `bound` (README: phase 2, multipleOf). */
export function withinBound(
  value: FieldValue,
  bound: FieldBound | undefined,
): boolean {
  if (!bound) {
    return true;
  }
  if (bound.kind === 'values') {
    return bound.values.includes(value);
  }
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= bound.min &&
    value <= bound.max &&
    value % bound.step === 0
  );
}
